import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type { CookiePreferences } from "@/hooks/useCookieConsent";

interface CookiePreferencesModalProps {
  /** 是否打开 */
  open: boolean;
  /** 关闭回调 */
  onClose: () => void;
  /** 当前偏好设置 */
  preferences: CookiePreferences;
  /** 保存偏好回调 */
  onSave: (prefs: CookiePreferences) => void;
}

/**
 * Cookie 类别配置
 */
const COOKIE_CATEGORIES = [
  {
    key: "necessary" as const,
    disabled: true, // 不可关闭
  },
  {
    key: "performance" as const,
    disabled: false,
  },
  {
    key: "functional" as const,
    disabled: false,
  },
  {
    key: "targeting" as const,
    disabled: false,
  },
];

/**
 * Cookie 偏好设置弹窗组件
 *
 * 允许用户精细控制各类 Cookie 的开关。
 * 必要 Cookie 始终开启且不可关闭。
 *
 * 特性：
 * - 四类 Cookie 开关
 * - 必要 Cookie 显示 "Always Active"
 * - 完整的 ARIA 可访问性支持
 * - 支持键盘操作（Escape 关闭、Tab 导航）
 */
export function CookiePreferencesModal({
  open,
  onClose,
  preferences,
  onSave,
}: CookiePreferencesModalProps) {
  const { t } = useTranslation("common");

  // 本地状态，用于编辑
  const [localPrefs, setLocalPrefs] = useState<CookiePreferences>(preferences);

  // 当 preferences 变化时同步本地状态
  useEffect(() => {
    setLocalPrefs(preferences);
  }, [preferences]);

  /**
   * 切换某个类别的开关
   */
  const handleToggle = (category: keyof CookiePreferences) => {
    if (category === "necessary") return; // 必要 Cookie 不可更改

    setLocalPrefs((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  /**
   * 保存设置
   */
  const handleSave = () => {
    onSave(localPrefs);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="sm:max-w-[420px] max-h-[90vh] overflow-y-auto bg-white dark:bg-black border-gray-200 dark:border-gray-800 p-0"
        aria-describedby="cookie-preferences-description"
      >
        <DialogHeader className="p-5 pb-0">
          <DialogTitle
            id="cookie-preferences-title"
            className="text-base font-semibold text-black dark:text-white"
          >
            {t("cookieConsent.modal.title")}
          </DialogTitle>
          <DialogDescription
            id="cookie-preferences-description"
            className="text-xs text-gray-500 dark:text-gray-400 mt-1"
          >
            {t("cookieConsent.modal.description")}
          </DialogDescription>
        </DialogHeader>

        {/* Cookie 类别列表 - Vercel 风格 */}
        <div className="px-5 py-4 space-y-3">
          {COOKIE_CATEGORIES.map(({ key, disabled }) => (
            <div
              key={key}
              className={cn(
                "flex items-start justify-between gap-3 p-3 rounded-md",
                "border border-gray-100 dark:border-gray-800",
                "bg-gray-50/50 dark:bg-gray-900/50"
              )}
            >
              <div className="flex-1 space-y-0.5">
                <Label
                  htmlFor={`cookie-${key}`}
                  className="text-xs font-medium text-black dark:text-white cursor-pointer"
                >
                  {t(`cookieConsent.categories.${key}.title`)}
                </Label>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
                  {t(`cookieConsent.categories.${key}.description`)}
                </p>
              </div>

              <div className="flex items-center shrink-0 pt-0.5">
                {disabled ? (
                  <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800">
                    {t("cookieConsent.modal.alwaysActive")}
                  </span>
                ) : (
                  <Switch
                    id={`cookie-${key}`}
                    checked={localPrefs[key]}
                    onCheckedChange={() => handleToggle(key)}
                    aria-label={t(`cookieConsent.categories.${key}.title`)}
                    className="data-[state=checked]:bg-black dark:data-[state=checked]:bg-white"
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* 保存按钮 - Vercel 风格 */}
        <div className="flex justify-end p-5 pt-3 border-t border-gray-100 dark:border-gray-800">
          <Button
            onClick={handleSave}
            className="bg-black hover:bg-gray-800 text-white dark:bg-white dark:hover:bg-gray-100 dark:text-black text-xs h-8 px-4 rounded-md font-medium transition-colors"
          >
            {t("cookieConsent.modal.confirmChoices")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CookiePreferencesModal;
